<template>
  <div id="update-user-profile">
    <h1 class="header-style">
      Profile Update
    </h1>
    <p class="subheader-style">
      Update your profile.
    </p>
    <div class="profile-section">
      <div class="profile-image">
        <img
          v-if="
            userDetails.profile_image !== null &&
              formData.profilePicture === null
          "
          :src="getImageUrl"
          alt=""
          width="80px"
        >
        <img
          v-else
          :src="profileImageSrc"
          alt=""
          width="80px"
        >
      </div>
      <div class="">
        <v-btn
          small
          elevation="0"
          class="edit-btn"
          @click="showPreview = true"
        >
          <v-icon> mdi-pencil-outline </v-icon> Edit
        </v-btn>
        <v-btn
          v-if="formData.profilePicture !== null"
          x-small
          fab
          elevation="0"
          color="red"
          class="ml-3 edit-btn"
          @click="formData.profilePicture = null"
        >
          <v-icon color="red">
            mdi-trash-can-outline
          </v-icon>
        </v-btn>
      </div>
    </div>
    <br>
    <div>
      <v-form
        ref="form"
        v-model="valid"
      >
        <v-text-field
          v-model="formData.firstName"
          label="First Name*"
          dense
          :rules="[(v) => !!v || 'This field is required']"
          color="#000000"
          light
          outlined
          background-color="#FFF"
          class="purple-input"
        />
        <v-text-field
          v-model="formData.lastName"
          label="Last Name*"
          :rules="[(v) => !!v || 'This field is required']"
          color="#000000"
          dense
          outlined
          background-color="#FFF"
          light
          class="purple-input"
        />
        <v-text-field
          v-model="formData.phoneNumber"
          label="Mobile"
          :rules="phoneNumberRules"
          color="#000000"
          outlined
          background-color="#FFF"
          dense
          disabled
          hint="Please contact support for number change."
          persistent-hint
          light
          class="purple-input"
        />
        <v-text-field
          v-model="formData.email"
          label="Email Address*"
          outlined
          background-color="#FFF"
          color="#000000"
          dense
          class="purple-input"
          light
        />
        <v-text-field
          v-model="formData.timezone"
          label="Timezone*"
          outlined
          background-color="#FFF"
          color="#000000"
          dense
          class="purple-input"
          light
          disabled
        />
      </v-form>
    </div>
    <v-btn
      class="btn-style"
      color="#64CA7F"
      elevation="0"
      @click="updateUserDetails()"
    >
      Update
    </v-btn>
    <v-btn
      class="btn-style grey-button"
      color="#E2E2E2"
      elevation="0"
      @click="cancel()"
    >
      Cancel
    </v-btn>
    <user-profile-image-review
      v-if="showPreview"
      @image-created="imageCreated"
      @closed="showPreview = false"
    />
    <centre-spinner :loading="loading" />
  </div>
</template>

<script>
import UserProfileImageReview from './UserProfileImageReview.vue';
import spinner from 'src/views/components/SpinnerCentre';
import Constants from 'src/constants';

export default {
  name: 'UserProfileUpdate',
  components: {
    'centre-spinner': spinner,
    'user-profile-image-review': UserProfileImageReview,
  },
  data: () => ({
    formData: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: null,
      profilePicture: null,
    },
    loading: false,
    valid: false,
    showPreview: false,
  }),
  computed: {
    profileImageSrc () {
      if (!this.formData.profilePicture) {
        return require('../../../assets/img/user-profile.png');
      }
      if (typeof this.formData.profilePicture === 'object') {
        return URL.createObjectURL(this.formData.profilePicture);
      }
      return this.formData.profilePicture;
    },
    firstOrganization () {
      return this.$store.getters.getSelectedOrganization;
    },
    userDetails () {
      return this.$store.getters['userprofile/getUserDetails'];
    },
    phoneNumberRules () {
      return [
        (v) => !!v || 'This field is required',
        (v) => (v && v.length <= 15) || 'Phone number exceeded the limit.',
      ];
    },
    getImageUrl () {
      const baseUrl = process.env.VUE_APP_STORAGE_API_URL;
      const image = this.userDetails.profile_image;
      if (image && image.file_path) {
        return `${baseUrl}/${image.file_path}`;
      }
      return require('../../../assets/img/user-profile.png');
    },
  },
  async mounted () {
    this.loading = true;
    await this.fetchUserDetails();
    this.loading = false;
  },
  methods: {
    async fetchUserDetails () {
      await this.$store.dispatch('userprofile/fetchUserDetails').then(() => {
        this.formData.firstName = this.userDetails.first_name;
        this.formData.lastName = this.userDetails.last_name;
        this.formData.email = this.userDetails.email;
        this.formData.phoneNumber = this.userDetails.phone_number;
        this.formData.timezone = this.userDetails.timezone;
      });
    },
    imageCreated (image) {
      this.formData.profilePicture = image;
      this.showPreview = false;
    },
    async updateUserDetails () {
      if (this.$refs.form.validate() === false) {
        this.$store.dispatch(
          'alert/onAlert',
          {
            message: 'Please fill the required fields before proceeding',
            type: Constants.ALERT_TYPE_ERROR,
          },
          { root: true },
        );
        return;
      }
      this.loading = true;
      if (this.formData.profilePicture !== null) {
        const formData = new FormData();
        formData.append('profile_image', this.formData.profilePicture);
        await this.$store.dispatch('userprofile/updateProfileImage', {
          appUserId: this.userDetails.external_id,
          formData,
        });
      }
      await this.$store
        .dispatch('userprofile/updateUserDetails', {
          details: {
            first_name: this.formData.firstName,
            last_name: this.formData.lastName,
            email_id: this.formData.email,
            emergency_contact: this.formData.emergency_contact,
          },
        })
        .then((response) => {
          this.$store.dispatch('alert/onAlert', {
            message: 'User details updated successfully.',
            type: Constants.ALERT_TYPE_SUCCESS,
          });
          this.$router.push({ name: 'User Profile' });
        })
        .catch((errors) => {
          this.loading = false;
        });
    },
    cancel () {
      this.loading = true;
      setTimeout(() => {
        this.$router.push({ name: 'User Profile' });
      }, 1000);
    },
  },
};
</script>

<style scoped>
#update-user-profile {
  padding-bottom: 80px;
}

.profile-image {
  height: 80px;
  width: 80px;
  border-radius: 100%;
  overflow: hidden;
  border: 1px solid #ccc;
}

.profile-section {
  display: flex;
  gap: 10px;
  align-items: end;
}

.edit-btn {
  text-transform: none;
  border: 1px solid #ccc;
  color: black !important;
  background: transparent !important;
}

.btn-group {
  width: 100%;
}
</style>
